// Visitors: "Who's here?" — tap faces to log a visit, "They've left" with how
// he found it, tips for guests, and Arlo's people (added from the phone's
// contacts or by hand, synced to every device).

import { Camera, Check, Contact, DoorOpen, Lightbulb, Plus, Search, Trash2, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RELATIONS, VISITOR_TIPS, VISITOR_TRAITS } from '../../content/visitors.ts';
import { useCollection, useDevice, useEvents, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { uid } from '../../data/ids.ts';
import { contactsToken, googlePhoto, listGoogleContacts, type GoogleContact } from '../../data/gcontacts.ts';
import { googleBlockedHere, googleOAuth, useGoogleClientId } from '../../data/google.ts';
import { APK_URL, NATIVE_CONTACTS_VERSION, androidAppVersion, inAndroidApp, pickNativeContacts } from '../../data/androidApp.ts';
import { devicePrefs } from '../../data/device.ts';
import { avatarFrom, canPickContacts, endVisit, openEarlierVisit, importContacts, removePerson, savePerson, startVisit, useImportStatus, clearImportStatus, type ImportOpts } from '../../data/visitors.ts';
import { joinNames } from '../../app/eventMeta.ts';
import { formatDuration, relativeDay, today } from '../../domain/time.ts';
import type { LogEvent, Reaction, Visitor } from '../../domain/types.ts';
import { initials, nameHue, openVisit, personStats, sameName, sortForGrid } from '../../domain/visitors.ts';
import { Button, Card, CardHeader, Field, Sheet } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';
import { openEditor, openNew } from '../log/EventEditor.tsx';

export function VisitorAvatar({ p, size = 56 }: { p: Pick<Visitor, 'name' | 'photo'>; size?: number }) {
  return p.photo ? (
    <img className="visitor-avatar" src={p.photo} alt="" width={size} height={size} style={{ width: size, height: size }} />
  ) : (
    <span className="visitor-avatar" aria-hidden style={{ width: size, height: size, fontSize: size * 0.36, background: `hsl(${nameHue(p.name)} 45% 46%)` }}>
      {initials(p.name)}
    </span>
  );
}

/** Open the phone's contact picker. What happened shows on screen (ImportStatusLine) and in a toast. */
async function pickFromContacts(people: Visitor[], opts?: ImportOpts): Promise<void> {
  try {
    const r = await importContacts(people, opts);
    if (r === undefined) toast({ message: 'Contacts aren’t available here', detail: 'Use Chrome or the Arlo HQ app on an Android phone.', tone: 'bad', duration: 9000 });
    else if (r.added) toast({ message: `${r.added} ${r.added === 1 ? 'person' : 'people'} added from contacts`, tone: 'good', icon: Contact, duration: 7000 });
  } catch (e) {
    toast({ message: 'Couldn’t get your contacts', detail: (e as Error).message, tone: 'bad', duration: 9000 });
  }
}

/**
 * How this device can pick from the phone's contacts: the Arlo HQ app's own
 * picker (reliable), the app but too old for it (offer the update), Chrome's
 * web picker, or not at all.
 */
type PhoneMode = 'native' | 'update' | 'web' | undefined;
function phoneContactsMode(): PhoneMode {
  // Any Android phone (not the kitchen tablet): ask the Arlo HQ app's own picker. If the app's missing or too old,
  // Chrome comes back with a note to update — so this never depends on the page knowing it's in the app.
  if (/Android/i.test(navigator.userAgent) && !devicePrefs().kiosk) return 'native';
  if (inAndroidApp() && androidAppVersion() < NATIVE_CONTACTS_VERSION) return 'update';
  return canPickContacts() ? 'web' : undefined;
}

function openPhoneContacts(people: Visitor[], mode: PhoneMode): void {
  if (mode === 'native') {
    clearImportStatus();
    void pickNativeContacts(devicePrefs().account?.name).catch((e: Error) => toast({ message: 'Couldn’t open your contacts', detail: e.message, tone: 'bad', duration: 9000 }));
  }
  else if (mode === 'update')
    toast({
      message: 'Update the Arlo HQ app to pick from your contacts',
      detail: 'The new version opens your phone’s own contacts list. Install it over the old one — everything stays.',
      tone: 'accent',
      duration: 15000,
      actions: [{ label: 'Get the update', onClick: () => (location.href = APK_URL) }],
    });
  else if (mode === 'web') void pickFromContacts(people);
}

/** The last contacts import, on screen — with a retry without photos when the picker came back empty. */
function ImportStatusLine({ people }: { people: Visitor[] }) {
  const st = useImportStatus();
  if (!st || Date.now() - st.at > 10 * 60_000) return null;
  return (
    <div className={`import-status tone-${st.tone}`} role="status">
      <span>{st.text}</span>
      {st.retry && (
        <Button size="sm" variant="primary" icon={Contact} onClick={() => pickFromContacts(people, st.retry!.opts)}>
          {st.retry.label}
        </Button>
      )}
    </div>
  );
}

/** The visit in progress, live. */
export function useVisit(): { open?: LogEvent<'visit'>; now: number } {
  const events = useEvents();
  const { now } = useLiveStatus(30_000);
  return { open: openVisit(events, now), now };
}

const REACTIONS: { value: Reaction; label: string; detail: string }[] = [
  { value: 'happy', label: 'Happy', detail: 'Relaxed, waggy, came and went' },
  { value: 'unsure', label: 'Unsure', detail: 'Hung back, needed time' },
  { value: 'worried', label: 'Worried', detail: 'Hid, barked or wouldn’t settle' },
];

/** "Who's here?" — arrivals, departures, and a way into "something new" instead. */
export function VisitorSheet({ open: show, onClose }: { open: boolean; onClose: () => void }) {
  const people = useCollection('people');
  const events = useEvents();
  const { open, now } = useVisit();
  const stats = useMemo(() => personStats(events), [events]);
  const grid = useMemo(() => sortForGrid(people, stats), [people, stats]);
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [tips, setTips] = useState(false);
  const here = open ? people.filter((p) => open.data.people.includes(p.id)) : [];
  const toggle = (id: string) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const phone = phoneContactsMode();
  const close = () => (setPicked([]), onClose());

  const arrive = () => {
    const ps = people.filter((p) => picked.includes(p.id));
    startVisit(ps, open);
    toast({ message: `${joinNames(ps.map((p) => p.name))} ${open ? 'joined the visit' : 'arrived'}`, detail: 'Tap the visitors line on Today when they leave.', tone: 'good', icon: DoorOpen });
    close();
  };
  const leave = (r?: Reaction) => {
    if (!open) return;
    endVisit(open, r, people);
    toast({ message: 'Visit logged', detail: r ? `${open.data.names.join(', ')} · ${r}${r === 'worried' ? ' — keep the next one short and calm' : ''}` : undefined, tone: 'good', icon: DoorOpen });
    close();
  };

  return (
    <>
      <Sheet open={show} onClose={close} title={open ? 'Visitors' : 'Who’s here?'} wide>
        {open && (
          <div className="visit-now">
            <div className="visit-now-faces">
              {here.map((p) => (
                <VisitorAvatar key={p.id} p={p} size={44} />
              ))}
              <span>
                <strong>{joinNames(open.data.names)}</strong>
                <span className="muted small"> · {now - open.at < 120_000 ? 'just arrived' : `here ${formatDuration(now - open.at)}`}</span>
              </span>
            </div>
            <span className="eyebrow">They’ve left — how did he find it?</span>
            <div className="visit-reactions">
              {REACTIONS.map((r) => (
                <button key={r.value} type="button" className={`visit-reaction tone-${r.value === 'happy' ? 'good' : r.value === 'unsure' ? 'warn' : 'bad'}`} onClick={() => leave(r.value)}>
                  <strong>{r.label}</strong>
                  <small>{r.detail}</small>
                </button>
              ))}
            </div>
            <span className="row visit-links">
              <button type="button" className="link small" onClick={() => leave()}>
                Just log that they’ve gone
              </button>
              <button type="button" className="link small" onClick={() => (close(), openEditor(open))}>
                Change when they arrived
              </button>
            </span>
            <span className="eyebrow visit-more">Someone else arrived?</span>
          </div>
        )}

        <div className="visitor-grid" role="group" aria-label="People">
          {grid
            .filter((p) => !open?.data.people.includes(p.id))
            .map((p) => {
              const on = picked.includes(p.id);
              return (
                <button key={p.id} type="button" className={`visitor-tile${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggle(p.id)}>
                  <span className="visitor-tile-face">
                    <VisitorAvatar p={p} size={64} />
                    {on && <Check aria-hidden className="visitor-tick" />}
                  </span>
                  <span className="visitor-name">{p.name.split(' ')[0]}</span>
                </button>
              );
            })}
          {grid.length > 0 && <button type="button" className="visitor-tile visitor-add" onClick={() => setAdding(true)}>
            <span className="visitor-tile-face">
              <UserPlus aria-hidden />
            </span>
            <span className="visitor-name">Someone new</span>
          </button>}
          {grid.length > 0 && <GoogleContactsButton people={people} tile />}
          {grid.length > 0 && phone && (
            <button
              type="button"
              className="visitor-tile visitor-add"
              onClick={() => openPhoneContacts(people, phone)}
            >
              <span className="visitor-tile-face">
                <Contact aria-hidden />
              </span>
              <span className="visitor-name">Phone contacts</span>
            </button>
          )}
        </div>
        {!grid.length && (
          // First time: make adding people the obvious thing to do.
          <div className="callout tone-info visit-empty">
            <p>
              <strong>Add the people who visit.</strong>{' '}
              Pick them from your {phone ? 'phone’s contacts' : 'Google Contacts'} — just their names and photos come across, and they appear on every device, the tablet too. Or add them one by one.
            </p>
            <div className="row">
              {phone && (
                <Button variant="primary" icon={Contact} onClick={() => openPhoneContacts(people, phone)}>
                  Phone contacts
                </Button>
              )}
              <GoogleContactsButton people={people} variant={phone ? 'outline' : 'primary'} />
              <Button variant="outline" icon={UserPlus} onClick={() => setAdding(true)}>
                Add someone
              </Button>
            </div>
            <ImportStatusLine people={people} />
          </div>
        )}
        {grid.length > 0 && <ImportStatusLine people={people} />}

        <div className="row visit-actions">
          {picked.length > 0 && (
            <Button variant="primary" icon={DoorOpen} onClick={arrive}>
              {joinNames(people.filter((p) => picked.includes(p.id)).map((p) => p.name.split(' ')[0]))} {open ? 'arrived too' : 'arrived'}
            </Button>
          )}
          <Button variant="ghost" icon={Lightbulb} onClick={() => setTips(true)}>
            Tips for visitors
          </Button>
        </div>
        <p className="muted small visit-footnote">
          Already been and gone?{' '}
          <button type="button" className="link" onClick={() => (close(), openEarlierVisit())}>
            Log an earlier visit
          </button>
        </p>
        <p className="muted small visit-footnote">
          Not a visitor?{' '}
          <button type="button" className="link" onClick={() => (close(), openNew('social', { itemId: '', reaction: 'happy' }, { title: 'New experience' }))}>
            Log a new experience instead
          </button>
        </p>
      </Sheet>
      {adding && <PersonSheet onClose={() => setAdding(false)} onSaved={(p) => setPicked((x) => [...x, p.id])} />}
      <VisitorTips open={tips} onClose={() => setTips(false)} />
    </>
  );
}

export function VisitorTips({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Tips for visitors">
      <ol className="visit-tips">
        {VISITOR_TIPS.map((t) => (
          <li key={t.title}>
            <strong>{t.title}</strong>
            <span className="muted small">{t.detail}</span>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}

/** Add or edit a person: name, photo, who they are, and what meeting them counts towards. */
export function PersonSheet({ person, onClose, onSaved }: { person?: Visitor; onClose: () => void; onSaved?: (p: Visitor) => void }) {
  const [p, setP] = useState<Visitor>(person ?? { id: uid(), name: '', source: 'manual', addedAt: Date.now() });
  const file = useRef<HTMLInputElement>(null);
  const toggleTrait = (id: string) => setP({ ...p, traits: p.traits?.includes(id) ? p.traits.filter((t) => t !== id) : [...(p.traits ?? []), id] });
  return (
    <Sheet
      open
      onClose={onClose}
      title={person ? p.name || 'Person' : 'Someone new'}
      footer={
        <>
          {person && (
            <Button
              variant="ghost"
              icon={Trash2}
              onClick={() => {
                removePerson(person.id);
                toast({ message: `${person.name} removed`, detail: 'Past visits stay in the log.', tone: 'good' });
                onClose();
              }}
            >
              Remove
            </Button>
          )}
          <Button
            variant="primary"
            disabled={!p.name.trim()}
            onClick={() => {
              const saved = { ...p, name: p.name.trim() };
              savePerson(saved);
              onSaved?.(saved);
              onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="form">
        <div className="person-head">
          <button type="button" className="person-photo" onClick={() => file.current?.click()} aria-label="Choose a photo">
            <VisitorAvatar p={{ name: p.name || '?', photo: p.photo }} size={72} />
            <Camera aria-hidden className="person-photo-icon" />
          </button>
          <input
            ref={file}
            type="file"
            accept="image/*"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setP({ ...p, photo: (await avatarFrom(f)) ?? p.photo });
            }}
          />
          <Field label="Name">
            <input className="input" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} autoFocus={!person} />
          </Field>
        </div>
        <Field label="Who are they?">
          <div className="chip-row">
            {RELATIONS.map((r) => (
              <button key={r.value} type="button" className={`chip${p.relation === r.value ? ' on' : ''}`} aria-pressed={p.relation === r.value} onClick={() => setP({ ...p, relation: p.relation === r.value ? undefined : r.value })}>
                {r.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Meeting them helps him get used to" hint="Ticks off his socialisation checklist when they visit.">
          <div className="chip-row">
            {VISITOR_TRAITS.map((t) => (
              <button key={t.id} type="button" className={`chip${p.traits?.includes(t.id) ? ' on' : ''}`} aria-pressed={!!p.traits?.includes(t.id)} onClick={() => toggleTrait(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </Field>
        <p className="muted small">Only their name and photo are kept — on your Arlo HQ server, shared with your devices.</p>
      </div>
    </Sheet>
  );
}

/** Socialise page: everyone he's met at home, and how he's getting on with them. */
export function PeopleCard() {
  const people = useCollection('people');
  const events = useEvents();
  const profile = useProfile();
  const guest = !!useDevice().guest;
  const stats = useMemo(() => personStats(events), [events]);
  const list = useMemo(() => sortForGrid(people, stats), [people, stats]);
  const [edit, setEdit] = useState<Visitor | 'new'>();
  const [who, setWho] = useState(false);
  const met = list.filter((p) => stats.get(p.id)?.visits).length;
  const phone = phoneContactsMode();
  return (
    <Card>
      <CardHeader
        title={`${profile.name}’s people`}
        icon={Users}
        sub={list.length ? `${met} of ${list.length} have visited. Add their traits and each visit ticks off his socialisation.` : 'Family and friends who come round. Add them from your phone’s contacts — they appear on the tablet too.'}
        action={
          !guest && (
            <Button size="sm" icon={DoorOpen} onClick={() => setWho(true)}>
              Visitor
            </Button>
          )
        }
      />
      {list.length > 0 && (
        <ul className="people-wall">
          {list.map((p) => {
            const s = stats.get(p.id);
            const last = s?.reactions.at(-1);
            return (
              <li key={p.id}>
                <button type="button" className="people-person" onClick={() => !guest && setEdit(p)}>
                  <VisitorAvatar p={p} size={52} />
                  <span>
                    <strong>{p.name}</strong>
                    <span className="muted small">
                      {s?.visits ? `${s.visits} visit${s.visits === 1 ? '' : 's'} · ${relativeDay(today(s.last!))}` : 'Not visited yet'}
                    </span>
                    {s && s.reactions.length > 0 && (
                      <span className="people-reactions" aria-label={`Recent: ${s.reactions.slice(-5).join(', ')}`}>
                        {s.reactions.slice(-5).map((r, i) => (
                          <i key={i} className={r} />
                        ))}
                        {last && <span className="small muted"> {last}</span>}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!guest && (
        <div className="row">
          <GoogleContactsButton people={people} variant="soft" />
          {phone && (
            <Button variant="outline" icon={Contact} onClick={() => openPhoneContacts(people, phone)}>
              Phone contacts
            </Button>
          )}
          <Button variant={phone ? 'outline' : 'soft'} icon={Plus} onClick={() => setEdit('new')}>
            Add someone
          </Button>
          {list.length > 0 && (
            <Button variant="ghost" icon={DoorOpen} onClick={openEarlierVisit}>
              Log an earlier visit
            </Button>
          )}
        </div>
      )}
      <ImportStatusLine people={people} />
      {!phone && !guest && <p className="muted small">In the Arlo HQ app on an Android phone you can pick people straight from your contacts.</p>}
      {edit && <PersonSheet person={edit === 'new' ? undefined : edit} onClose={() => setEdit(undefined)} />}
      <VisitorSheet open={who} onClose={() => setWho(false)} />
    </Card>
  );
}

/** Google Contacts available here (sign-in set up, and not an embedded browser Google refuses)? */
function useGoogleContacts(): string | undefined {
  const id = useGoogleClientId();
  useEffect(() => {
    if (id) void googleOAuth().catch(() => undefined); // load Google's script ahead of the tap
  }, [id]);
  return id && !googleBlockedHere() ? id : undefined;
}

/** "From Google Contacts": Google's sign-in from the tap, then our own searchable list. */
export function GoogleContactsButton({ people, variant = 'outline', tile }: { people: Visitor[]; variant?: 'primary' | 'outline' | 'soft'; tile?: boolean }) {
  const clientId = useGoogleContacts();
  const [list, setList] = useState<GoogleContact[] | 'loading'>();
  if (!clientId) return null;
  const open = () => {
    setList('loading');
    contactsToken(clientId)
      .then(listGoogleContacts)
      .then(setList)
      .catch((e: Error) => {
        setList(undefined);
        if (e.message !== 'Closed before finishing') toast({ message: 'Couldn’t get your Google Contacts', detail: e.message, tone: 'bad', duration: 12000 });
      });
  };
  return (
    <>
      {tile ? (
        <button type="button" className="visitor-tile visitor-add" onClick={open}>
          <span className="visitor-tile-face">
            <Contact aria-hidden />
          </span>
          <span className="visitor-name">Google Contacts</span>
        </button>
      ) : (
        <Button variant={variant} icon={Contact} onClick={open}>
          From Google Contacts
        </Button>
      )}
      {list && <GoogleContactsSheet list={list} people={people} onClose={() => setList(undefined)} />}
    </>
  );
}

function GoogleContactsSheet({ list, people, onClose }: { list: GoogleContact[] | 'loading'; people: Visitor[]; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const have = (c: GoogleContact) => people.some((p) => sameName(p.name, c.name));
  const shown = list === 'loading' ? [] : list.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 200);
  const add = () => {
    if (list === 'loading') return;
    const chosen = list.filter((c) => picked.includes(c.id) && !have(c));
    const made = chosen.map((c) => ({ person: { id: uid(), name: c.name, source: 'contacts' as const, addedAt: Date.now() }, photo: c.photo }));
    for (const m of made) savePerson(m.person);
    toast({ message: `${made.length} ${made.length === 1 ? 'person' : 'people'} added`, detail: 'They’re on the tablet too.', tone: 'good', icon: Contact });
    onClose();
    // Photos after the names, one by one.
    void (async () => {
      for (const m of made) if (m.photo) savePerson({ ...m.person, photo: await googlePhoto(m.photo) });
    })();
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title="Google Contacts"
      wide
      footer={
        <Button variant="primary" icon={UserPlus} disabled={!picked.length} onClick={add}>
          {picked.length ? `Add ${picked.length}` : 'Tap people to add'}
        </Button>
      }
    >
      {list === 'loading' ? (
        <p className="muted">Waiting for Google…</p>
      ) : (
        <>
          <label className="search">
            <Search aria-hidden />
            <input className="input" placeholder="Search your contacts" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </label>
          {!list.length && <p className="muted small">No contacts with names in this Google account.</p>}
          <ul className="gcontacts">
            {shown.map((c) => {
              const added = have(c);
              const on = picked.includes(c.id);
              return (
                <li key={c.id}>
                  <button type="button" className={`gcontact${on ? ' on' : ''}`} disabled={added} aria-pressed={on} onClick={() => setPicked((x) => (on ? x.filter((y) => y !== c.id) : [...x, c.id]))}>
                    <VisitorAvatar p={{ name: c.name, photo: c.photo }} size={40} />
                    <span>{c.name}</span>
                    {added ? <small className="muted">Added</small> : <span className={`gcontact-check${on ? ' on' : ''}`}>{on && <Check aria-hidden />}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="muted small">Only the names and photos of the people you add are kept, on your Arlo HQ server.</p>
        </>
      )}
    </Sheet>
  );
}
